import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Printer, ArrowLeft, Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

// PDF server-side (reportlab). NIENTE anteprima nel browser: appena caricato,
// il PDF viene aperto in una NUOVA TAB dove il browser mostra il suo viewer nativo
// (dal quale l'operatore usa la funzione di stampa integrata).
export default function StampaOrdineEstrusioneA4() {
    const { id } = useParams();
    const nav = useNavigate();
    const [numero, setNumero] = useState("");
    const [loading, setLoading] = useState(true);
    const [pdfUrl, setPdfUrl] = useState(null);
    const openedRef = useRef(false);

    useEffect(() => {
        let objectUrl = null;
        (async () => {
            setLoading(true);
            try {
                const [{ data: ord }, res] = await Promise.all([
                    api.get(`/ordini-estrusione/${id}`),
                    api.get(`/ordini-estrusione/${id}/pdf`, { responseType: "blob" }),
                ]);
                setNumero(ord.numero || "");
                const blob = new Blob([res.data], { type: "application/pdf" });
                objectUrl = URL.createObjectURL(blob);
                setPdfUrl(objectUrl);
                // Apri automaticamente in una nuova tab (solo la prima volta)
                if (!openedRef.current) {
                    openedRef.current = true;
                    const w = window.open(objectUrl, "_blank");
                    if (!w) {
                        // Pop-up bloccato dal browser: mostro il pulsante manuale
                        toast.warning("Il browser ha bloccato l'apertura automatica. Usa il pulsante Apri PDF.");
                    }
                }
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [id]);

    const apri = () => {
        if (!pdfUrl) return;
        window.open(pdfUrl, "_blank");
    };
    const scarica = () => {
        if (!pdfUrl) return;
        const a = document.createElement("a");
        a.href = pdfUrl; a.download = `${numero || "ordine-estrusione"}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6" data-testid="stampa-oe-pdf">
            <div className="max-w-lg w-full bg-slate-900 border border-slate-800 rounded-xl p-8 text-center space-y-6">
                <div className="text-[10px] tracking-[0.3em] font-mono text-amber-400/80">PRODUX · STAMPA ORDINE ESTRUSIONE</div>
                <div>
                    <div className="text-white font-display text-2xl font-extrabold">{numero || "Caricamento…"}</div>
                    <div className="text-slate-400 text-sm mt-1">Foglio macchina in formato PDF</div>
                </div>
                {loading && (
                    <div className="text-slate-400 text-sm animate-pulse">Generazione PDF in corso…</div>
                )}
                {!loading && pdfUrl && (
                    <>
                        <div className="text-emerald-400 text-sm flex items-center justify-center gap-2">
                            <ExternalLink size={14}/> PDF aperto nella nuova scheda. Stampa da lì.
                        </div>
                        <div className="flex flex-wrap gap-2 justify-center">
                            <Button onClick={apri} data-testid="btn-open-pdf"
                                    className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                                <Printer size={14}/> Apri PDF (o Stampa)
                            </Button>
                            <Button variant="outline" onClick={scarica} data-testid="btn-download-pdf"
                                    className="bg-transparent border-slate-700 text-slate-300 gap-2">
                                <Download size={14}/> Scarica
                            </Button>
                        </div>
                    </>
                )}
                {!loading && !pdfUrl && (
                    <div className="text-red-400 text-sm">Errore nella generazione del PDF</div>
                )}
                <div className="pt-2 border-t border-slate-800">
                    <Button variant="outline" onClick={()=>nav(-1)} data-testid="btn-back-pdf"
                            className="bg-transparent border-slate-700 text-slate-400 gap-2">
                        <ArrowLeft size={14}/> Torna agli ordini
                    </Button>
                </div>
            </div>
        </div>
    );
}
